import { InsightTableGroupBy } from 'modules/finance/components/FinancialWorkspace/types/table'
import { FinancialWorkspaceRecord } from 'modules/finance/components/FinancialWorkspaceDataCollection/types'

/**
 * Determines the resource filter key based on groupBy type
 * Used to filter children query by the parent's ID
 */
export const getResourceFilterKey = (
  groupBy: InsightTableGroupBy
): 'employeeIds' | 'locationIds' | 'legalEntityIds' | 'costCenterIds' | 'managerIds' | null => {
  switch (groupBy) {
    case InsightTableGroupBy.Employee:
      return 'employeeIds'
    case InsightTableGroupBy.Location:
      return 'locationIds'
    case InsightTableGroupBy.LegalEntity:
      return 'legalEntityIds'
    case InsightTableGroupBy.CostCenter:
      return 'costCenterIds'
    case InsightTableGroupBy.Manager:
      return 'managerIds'
    default:
      return null
  }
}

/**
 * Finance type identifiers used in Total view
 * These match the serialized T::Enum values from the backend:
 * - FinancialWorkspace::ValueObjects::FinanceType::Payroll.serialize => 'payroll'
 * - FinancialWorkspace::ValueObjects::FinanceType::Expense.serialize => 'expense'
 * - FinancialWorkspace::ValueObjects::FinanceType::Agreement.serialize => 'agreement'
 * - FinancialWorkspace::ValueObjects::FinanceType::Compensations.serialize => 'compensations'
 */
export const FINANCE_TYPE_IDS = {
  PAYROLL: 'payroll',
  COMPENSATIONS: 'compensations',
  EXPENSES: 'expense',
  AGREEMENTS: 'agreement',
} as const

/**
 * Determines if an item is an expenses row in Total view
 */
export const isExpensesRow = (item: FinancialWorkspaceRecord): boolean => {
  const itemId = String(item.id)
  return itemId === FINANCE_TYPE_IDS.EXPENSES || itemId.endsWith(`::${FINANCE_TYPE_IDS.EXPENSES}`)
}

export const referenceRowType = (item: FinancialWorkspaceRecord) => {
  if (item.id === "agreement") return "striped"
  return "none"
}

/**
 * Determines if an item should show the expand/collapse button
 *
 * For InsightTableItems (Payroll): Check pre-loaded children
 */
export const shouldShowExpanderForInsightTable = (item: FinancialWorkspaceRecord): boolean => {
  // For InsightTableItems, children are pre-loaded
  return !!(item.children && item.children.length > 0)
}

const hasNonZeroValues = (item: FinancialWorkspaceRecord): boolean => {
  return !!item.periodValues && item.periodValues.some((pv) => Number(pv.amountInCents) !== 0) ||
    !!item.comparisonValues && item.comparisonValues.some((cv) => Number(cv.amountInCents) !== 0)
}

/**
 * Determines if an item should show expander for FinanceFactSummaries (Employee Costs)
 *
 * Logic:
 * - For non-Total views (Cost Center, Employee, etc.): Always show expander
 *   (rows expand to show Payroll/Expenses/Agreements breakdown for that resource)
 * - For Total view:
 *   - Expenses row: Show expander (expands to CustomCategory breakdown)
 *   - Payroll/Compensation/Agreements rows: No expander
 *   - Child rows (CustomCategory results): No expander (leaf nodes)
 */
export const shouldShowExpanderForFinanceSummaries = (
  groupBy: InsightTableGroupBy,
  item: FinancialWorkspaceRecord
): boolean => {
  // For Total view, only Expenses rows have expanders
  if (groupBy === InsightTableGroupBy.Total) {
    // Check if item has data to expand
    const hasData = hasNonZeroValues(item)
    if (!hasData) return false
    // Only expenses rows can expand in Total view
    return isExpensesRow(item)
  }

  if (item.level === 0) return true // Top-level rows always show expander in non-Total views
  if (!item.hasChildren) return false

  return hasNonZeroValues(item)
}
